import type { KeyboardEvent } from "react";
import type { ClusterRunResponse } from "src/queries/compute/compute.types";
import { formatPitForDisplay } from "src/utils/time";

/**
 * Creates an isOptionEqualToValue function that handles both string and object values
 * @param keyName - The key name to use for comparison
 * @returns A function that determines if an option equals the current value
 */
export const createIsOptionEqualToValue = (keyName: string = "run_time") => {
  return (option: ClusterRunResponse, value: ClusterRunResponse | string) => {
    // If value is a string, compare it directly to the option's keyName field
    if (typeof value === "string") {
      return option[keyName as keyof ClusterRunResponse] === value;
    }
    // If value is an object, compare the keyName fields
    return (option as any)[keyName] === (value as any)[keyName];
  };
};

/**
 * Creates a getOptionKey function that extracts the key from an option
 * @param keyName - The key name to extract from the option
 * @returns A function that extracts the key from an option
 */
export const createGetOptionKey = (keyName: string = "run_time") => {
  return (option: ClusterRunResponse | string): string | number => {
    if (typeof option === "string") {
      return option;
    }
    return (option as any)[keyName];
  };
};

/**
 * Creates a getOptionLabel function that handles both string and object options
 * @param keyName - The key name to extract from object options
 * @returns A function that returns the formatted label for an option
 */
export const createGetOptionLabel = (keyName: string = "run_time") => {
  return (option: ClusterRunResponse | string) => {
    // If option is a string (when value prop is a string), format it directly
    if (typeof option === "string") {
      return formatPitForDisplay(option) ?? "";
    }
    // If option is an object, extract the keyName field and format it
    return formatPitForDisplay((option as any)[keyName]) ?? "";
  };
};

/**
 * Creates a keyboard event handler that re-dispatches Escape key events at the window level
 * @returns A keyboard event handler function
 */
export const createKeyDownHandler = () => {
  return (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      // Re-dispatch the event at the window level
      window.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape" }));
    }
  };
};

/**
 * Focuses an input element with proper timing and conditions
 * @param inputRef - The ref to the input element
 * @param shouldFocus - Whether the input should be focused
 * @param isLoading - Whether the component is in a loading state
 */
export const focusInput = (
  inputRef: { current: HTMLInputElement | null },
  shouldFocus: boolean,
  isLoading: boolean
): (() => void) | void => {
  if (shouldFocus && inputRef.current && !isLoading) {
    // Use setTimeout to ensure the component is fully rendered
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
    return () => clearTimeout(timer);
  }
};
